import dayjs from 'dayjs';
import type {RunTicket, RunTicketScanStatus} from '@/types/well';
import type {RunTicket_AllWhereQuery, RunTicketCreateUpdateInput} from '@/graphql/generated/graphql.ts';
import type {RunTicketDraft} from '@/pages/RunTicketFormPanel.tsx';

// scanStatus wire-format mismatch, confirmed live 2026-08-24 (Duane): the
// real backend scan job returns "NeedsReviewed" and "SCANNED", not this
// client's own canonical 'NeedsReview'/'Scanned' spellings (see the long
// comment on types/well.ts's RunTicketScanStatus for the full context,
// including the open question of whether 'Scanned' belongs in
// useRunTicketReconciliation's SCAN_IN_FLIGHT_STATUSES). "Pending" and
// "Approved" already match and pass through unchanged both ways.
//
// Normalizing at this one GraphQL boundary — read side in toRunTicket,
// write side in toRunTicketInput — means every `=== 'NeedsReview'`
// comparison already spread across the app keeps working unmodified,
// and a save that round-trips a scan status (e.g. RunTicketFormPanel's
// Approve & Save) sends the same spelling the real backend job would
// have written, rather than a client-only one.
function normalizeScanStatus(raw: string | null | undefined): RunTicketScanStatus | null {
   if (raw === 'NeedsReviewed') return 'NeedsReview';
   if (raw === 'SCANNED') return 'Scanned';
   return (raw as RunTicketScanStatus | null) ?? null;
}

// Exported (unlike normalizeScanStatus) so a caller building a wire-format
// scanStatus value outside of toRunTicketInput can still reach the same
// denormalization if it ever needs to. state/runTicketPhotoSync.ts's sparse
// photo-attach payload is one such caller as of 2026-09-09 — it sends a
// literal 'Pending' (see its own doc comment for why), which happens to
// pass through denormalizeScanStatus unchanged, but is written as a plain
// literal there rather than routed through this function since 'Pending'
// never needs the NeedsReview/Scanned wire-format translation this
// function exists for.
export function denormalizeScanStatus(status: RunTicketScanStatus | null | undefined): string | undefined {
   if (status === 'NeedsReview') return 'NeedsReviewed';
   if (status === 'Scanned') return 'SCANNED';
   return status ?? undefined;
}

// Folds a fresh scan result into a form draft that's already open — used
// when a Retake finishes attaching (RunTicketFormPanel.handlePhotoChange).
// The backend overwrites every field the scanner reads on each scan (see
// docs/run-ticket-scan-spec.md), so the server row is already updated by
// the time this runs; the open form just needs to show it.
//
// A field is "untouched" when the draft still equals `baseline` (the draft
// as last seeded from the server) — those take the scanned value. A field
// the user has typed into since (draft !== baseline) is kept as-is, so an
// in-progress edit isn't yanked out from under them; if they Save, that
// edit goes to the server like any other (toRunTicketInput diffs against
// the ticket the form opened from). id and photoUrl are never merged (the
// form owns the photo preview itself); scanStatus always follows the scan.
//
// Returns the merged draft plus the new baseline (taken fields advance to
// the scanned value so a LATER rescan compares against what was merged).
export function mergeScanIntoDraft(
   current: RunTicketDraft,
   baseline: RunTicketDraft,
   scanned: RunTicketDraft,
): {draft: RunTicketDraft; baseline: RunTicketDraft} {
   const draft: RunTicketDraft = {...current};
   const nextBaseline: RunTicketDraft = {...baseline};
   const assign = <K extends keyof RunTicketDraft>(key: K, value: RunTicketDraft[K]) => {
      draft[key] = value;
      nextBaseline[key] = value;
   };
   for (const key of Object.keys(scanned) as (keyof RunTicketDraft)[]) {
      if (key === 'id' || key === 'photoUrl') continue;
      if (key === 'scanStatus' || current[key] === baseline[key]) assign(key, scanned[key]);
   }
   return {draft, baseline: nextBaseline};
}

// Human-readable label for a RunTicketScanStatus — used anywhere the raw
// value is shown to a user (RunTicketsPage's Scan Status column and CSV/
// PDF export).
//
// 'NeedsReview' reformats to "Needs Review" with a space (Duane,
// 2026-08-24) since the canonical value is one run-together word.
//
// CHANGED 2026-08-25 per Duane's explicit follow-up: 'Scanned' now ALSO
// displays as "Needs Review" — reversing this file's own same-day note
// (a few commits earlier) that the column should show a ticket's literal
// status rather than the app's derived verdict. Duane asked for the
// derived verdict here after all, since 'Scanned' IS the real "needs
// review" status (see isAwaitingReview below) and showing the raw word
// "Scanned" read as if nothing needed doing. Every other canonical value
// still reads correctly as-is.
export function formatScanStatus(status: RunTicketScanStatus | null | undefined): string | null {
   if (status == null) return null;
   if (isAwaitingReview(status)) return 'Needs Review';
   return status;
}

// Added 2026-08-25 per Duane: corrected 2026-08-24's assumption (see
// types/well.ts's RunTicketScanStatus doc comment) that 'Scanned' was a
// settled, no-action-needed state — Duane confirmed the real tickets
// he's reviewing carry scanStatus 'Scanned', not 'NeedsReview'. Every
// "does this ticket need review" check in the app now goes through this
// one helper instead of comparing `scanStatus === 'NeedsReview'`
// directly (runTicketAtoms.ts's needsReviewCountAtom, RunTicketsPage's
// count/quick-filter, RunTicketFormPanel's review banner/Approve flow,
// useRunTicketReconciliation's notification, and — added same day,
// later — formatScanStatus's display label above) so a future
// correction like this one only has to change one place. 'NeedsReview'
// stays in the OR rather than being dropped outright — the earlier
// scanStatus wire-format fix was built off an actually-observed
// "NeedsReviewed" wire value, so until Duane says otherwise both are
// treated as meaning the same thing.
export function isAwaitingReview(status: RunTicketScanStatus | null | undefined): boolean {
   return status === 'NeedsReview' || status === 'Scanned';
}

// Same convention as lib/wellMapping.ts's toWell / adminMappers.ts's
// toAdminWell — turns a real RunTicket row (see
// generated-operations/RunTicket_All.graphql's selection set, which
// mirrors RunTicket_CreateUpdate/RunTicket_AllWhere 1:1) into the
// RunTicket shape the rest of the app renders (types/well.ts).
//
// wellName is denormalized in here (RunTicket itself carries only
// wellId) the same way BarrelTest/DailyReportDraft do — callers pass a
// wellId → wellName lookup built from Well_AllDocument, same pattern as
// useRunTicketReconciliation's existing wellNameById.
//
// status defaults to 'Matched' here — a deliberate placeholder, not a
// stale gap: this function only ever sees one raw RunTicket row, with no
// access to the matching Daily Report window it'd need to know whether
// any report actually cites this ticket's number. The real value comes
// from useRunTicketReconciliation, which has both the ticket list and
// the Daily Report window loaded already — see
// runTicketAtoms.ts's unmatchedTicketIdsAtom. RunTicketsPage's
// withLiveStatus overlays 'Unmatched' on top of this placeholder when it
// applies, so nothing reads this function's own 'Matched' default as if
// it were a real cross-reference result.
//
// A varianceBbls field/'Balanced'|'Variance' distinction briefly lived
// here too (SRS 3.8, added 2026-08-20) — removed 2026-08-21 per Duane,
// see types/well.ts's RunTicketStatus doc comment for why.
//
// Row's audit columns (createdByUserId/createdDateTime/updatedByUserId/
// updatedDateTime, added 2026-09-14) are typed optional here even though
// RunTicket_AllWhere.graphql's own generated row always has them (schema
// marks all four NOT NULL) — this same function is also called with a
// RunTicket_CreateUpdateMutation/RunTicket_AttachPhotoMutation response
// row (see RunTicketsPage.tsx/runTicketPhotoSync.ts), and
// RunTicket_AttachPhoto.graphql's header comment explains in detail why
// its selection deliberately excludes these four fields (a failed
// resolver on any NOT NULL field null-propagates the whole response
// object — Duane hit this for real 2026-08-21). Widening this type to
// require them here would force adding them to that mutation too and
// reintroduce the exact bug that file's comment documents fixing.
type RunTicketAuditRow =
   | (Omit<
        NonNullable<NonNullable<RunTicket_AllWhereQuery['runTicket_AllWhere']>[number]>,
        'createdByUserId' | 'createdDateTime' | 'updatedByUserId' | 'updatedDateTime'
     > & {
        createdByUserId?: unknown;
        createdDateTime?: unknown;
        updatedByUserId?: unknown;
        updatedDateTime?: unknown;
     })
   | null;

export function toRunTicket(row: RunTicketAuditRow, wellNameById: Map<string, string>): RunTicket | null {
   if (!row) return null;
   const wellId = String(row.wellId ?? '');
   return {
      id: String(row.id),
      wellId,
      wellName: wellNameById.get(wellId) ?? '',
      ticketNumber: row.ticketNumber ?? '',
      confirmationNumber: row.confirmationNumber ?? null,
      location: row.location ?? '',
      tank: row.tank ?? '',
      operator: row.operator ?? '',
      leaseId: row.leaseId ?? null,
      countyState: row.countyState ?? '',
      purchaser: row.purchaser ?? '',
      transportationCompany: row.transportationCompany ?? '',
      pickupDate: row.pickupDate ? String(row.pickupDate) : '',
      topGauge: row.topGauge ?? '',
      bottomGauge: row.bottomGauge ?? '',
      tankBottom: row.tankBottom ?? 0,
      obsGravity: row.obsGravity ?? 0,
      correctedGravity: row.correctedGravity ?? 0,
      obsTemperature: row.obsTemperature ?? 0,
      topTemp: row.topTemp ?? 0,
      bottomTemp: row.bottomTemp ?? 0,
      bswPct: row.bswPct ?? 0,
      govBarrels: row.govBarrels ?? 0,
      gsvBarrels: row.gsvBarrels ?? 0,
      nsvBarrels: row.nsvBarrels ?? 0,
      sealOff: row.sealOff ?? '',
      sealOffTime: row.sealOffTime ? String(row.sealOffTime) : null,
      sealOn: row.sealOn ?? '',
      sealOnTime: row.sealOnTime ? String(row.sealOnTime) : null,
      // truck/trailer/sid/odometer/driverName/latitude/longitude
      // RETIRED entirely 2026-08-24 — see types/well.ts's doc comment.
      // No longer selected in RunTicket_All.graphql, so nothing to read
      // off `row` here even though the live schema still has them.
      // Real persisted photo, once one's been scanned/uploaded — see the
      // photoUrl doc comment on types/well.ts's RunTicket. Object-URL
      // previews (RunTicketFormPanel/ScanTicketButton) still take
      // priority client-side until the next refetch brings this back.
      //
      // Reads off preSignedUrl, not imageS3 (swapped 2026-08-24) — imageS3
      // turned out to be a private-bucket link the client has no
      // GetObject grant for, so it rendered as a dead "Access Denied"
      // image/link. preSignedUrl is a real presigned GetObject URL, but
      // it's only valid for ~15 minutes per backend — this value goes
      // stale if held onto past that. Don't cache it (offline storage,
      // long-lived component state, etc.); it's only good for the
      // lifetime of whatever query result it came from.
      photoUrl: row.ticketImageData?.preSignedUrl ?? undefined,
      scanStatus: normalizeScanStatus(row.scanStatus),
      status: 'Matched',
      // Real audit columns, added to the query 2026-09-14 — see
      // RunTicket_AllWhere.graphql's header comment. Always present on a
      // real row (schema marks all four NOT NULL), unlike the synthetic
      // rows fromQueuedRunTicketInput below builds for a still-queued
      // offline create.
      createdByUserId: row.createdByUserId != null ? String(row.createdByUserId) : null,
      createdDateTime: row.createdDateTime != null ? String(row.createdDateTime) : null,
      updatedByUserId: row.updatedByUserId != null ? String(row.updatedByUserId) : null,
      updatedDateTime: row.updatedDateTime != null ? String(row.updatedDateTime) : null,
   };
}

// Server timestamps arrive as UTC ISO strings (e.g. '2026-10-02T00:44:00.000Z').
// The draft's wire format is 'YYYY-MM-DDTHH:mm' LOCAL-naive, so convert to the
// browser's local time rather than slicing the UTC string — slicing showed the
// edit panel (and "Recent tickets") in UTC, up to a day ahead of the table,
// which formats through dayjs in local time. FIXED 2026-10-16.
export function isoToLocalDraft(iso: string | null | undefined): string {
   if (!iso) return '';
   if (iso.length === 10) return `${iso}T00:00`;
   const d = dayjs(iso);
   return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : iso.slice(0, 16);
}

// 'YYYY-MM-DDTHH:mm' local-naive strings for the three timestamp fields
// below — same shape the native datetime-local input used to hand back
// directly; now produced by formatting each DateTimePicker's Dayjs value.
// Kept as the draft's wire format since toRunTicketInput's
// `new Date(draft.X).toISOString()` already expects exactly this.
//
// The non-null half of RunTicketFormPanel's own toDraft (see that file's
// comment on why the split) — also used directly by
// RunTicketsPage.handleSaveTicket to snapshot the ticket an Edit drawer
// started from, as toRunTicketInput's diff baseline.
export function runTicketToDraft(ticket: RunTicket): RunTicketDraft {
   return {
      id: ticket.id,
      wellId: ticket.wellId,
      ticketNumber: ticket.ticketNumber,
      confirmationNumber: ticket.confirmationNumber != null ? String(ticket.confirmationNumber) : '',
      location: ticket.location,
      tank: ticket.tank,
      operator: ticket.operator,
      leaseId: ticket.leaseId != null ? String(ticket.leaseId) : '',
      countyState: ticket.countyState,
      purchaser: ticket.purchaser,
      transportationCompany: ticket.transportationCompany,
      pickupDate: isoToLocalDraft(ticket.pickupDate),
      topGauge: ticket.topGauge,
      bottomGauge: ticket.bottomGauge,
      tankBottom: String(ticket.tankBottom),
      obsGravity: String(ticket.obsGravity),
      correctedGravity: String(ticket.correctedGravity),
      obsTemperature: String(ticket.obsTemperature),
      topTemp: String(ticket.topTemp),
      bottomTemp: String(ticket.bottomTemp),
      bswPct: String(ticket.bswPct),
      govBarrels: String(ticket.govBarrels),
      gsvBarrels: String(ticket.gsvBarrels),
      nsvBarrels: String(ticket.nsvBarrels),
      sealOff: ticket.sealOff,
      sealOffTime: isoToLocalDraft(ticket.sealOffTime),
      sealOn: ticket.sealOn,
      sealOnTime: isoToLocalDraft(ticket.sealOnTime),
      photoUrl: ticket.photoUrl ?? '',
      scanStatus: ticket.scanStatus,
   };
}

// Builds the RunTicketCreateUpdateInput this app can actually populate
// today. `id` is the create-or-update key, same upsert convention as
// Well/User/DailyWellReport — pass the existing ticket's id to edit, or
// a fresh crypto.randomUUID() to create (as of 2026-08-20, RunTicketFormPanel
// seeds that id itself, early, rather than the caller generating one at
// save time — see its toDraft).
//
// CONFIRMED 2026-08-27 (live test against the real backend — see
// run_ticket_photo_attach_race.md): runTicket_CreateUpdate is a genuine
// partial update, an omitted field is left alone, not nulled out. This
// function used to also build and send `ticketImageS3Data` off a
// `ticketImageKey` the draft carried purely so an ordinary field edit
// couldn't blank out an already-attached photo — that's no longer
// necessary (and was a real staleness risk: a stale in-memory copy of
// the key could clobber a newer photo attached elsewhere). This form
// never sets the photo directly — state/runTicketPhotoSync.ts's own
// sparse `{id, ticketImageS3Data}` payload is now the only place that
// field gets sent — so it's omitted here entirely.
//
// `original` (added 2026-08-31, per Duane): the same class of staleness
// risk the photo field had — flagged in run_ticket_photo_attach_race.md
// as "not isolated to one field, not observed yet" — applies to every
// other field too, since the AI scan job can write extracted values onto
// a ticket asynchronously (confirmed in dashboard_queries.md) with no
// client trigger. Before this, every save resent the FULL form,
// including fields the user never touched — so a scan that wrote e.g. a
// corrected `topGauge` while the office had this ticket's Edit drawer
// open for an unrelated reason (fixing `sealOff`, say) would get its
// fresh scanned value silently overwritten the moment that unrelated
// edit was saved. `original` is a snapshot of what this form loaded
// (RunTicketFormPanel.toDraft(ticket) — see RunTicketsPage.handleSaveTicket,
// the only caller that has one), taken BEFORE the user made any edits;
// `changed()` below compares each field's raw draft value against it and
// omits (rather than resends) anything that didn't change, so a
// concurrent scan write to a field this save doesn't touch survives.
// `original` is undefined/null on create (nothing to diff against —
// every field is new) and for any caller that doesn't have a baseline,
// in which case this behaves exactly as before: always resend.
//
// scanStatus is NOT diffed — it's still included every time, because
// this exact save action does sometimes intentionally change it (the
// Approve flow, RunTicketFormPanel's handleSave flipping
// 'NeedsReview'/'Scanned' to 'Approved') — draft.scanStatus is always
// the ticket's real current-or-approved status, never a stale defensive
// echo, so there's nothing to protect it from.
export function toRunTicketInput(
   draft: RunTicketDraft,
   id: string,
   original?: RunTicketDraft | null,
): RunTicketCreateUpdateInput {
   const bswPct = Number(draft.bswPct || 0);
   const gsvBarrels = Number(draft.gsvBarrels || 0);
   const nsvBarrelsRaw = draft.nsvBarrels.trim();
   const nsvBarrels = nsvBarrelsRaw
      ? Number(draft.nsvBarrels)
      : Math.round(gsvBarrels * (1 - bswPct / 100) * 100) / 100;

   // Compares the RAW draft string against original's, before any
   // Number()/trim() normalization below — a field the user never
   // touched (even one whose normalized value happens to differ, e.g.
   // "3.10" vs "3.1") is never mistaken for an edit.
   const changed = <K extends keyof RunTicketDraft>(key: K) => !original || draft[key] !== original[key];

   // NSV is a special case: when the field itself is blank, its value
   // isn't the user's own input, it's DERIVED from GSV/BS&W (see
   // suggestedNsv in RunTicketFormPanel.tsx) — so it needs resending
   // whenever either of those changed too, not just when nsvBarrels
   // itself did, or a GSV/BS&W edit's effect on the suggested NSV would
   // get dropped by the diff below.
   const nsvBarrelsChanged = changed('nsvBarrels') || (!nsvBarrelsRaw && (changed('gsvBarrels') || changed('bswPct')));

   return {
      id,
      scanStatus: denormalizeScanStatus(draft.scanStatus),
      wellId: changed('wellId') ? draft.wellId || undefined : undefined,
      ticketNumber: changed('ticketNumber') ? draft.ticketNumber || undefined : undefined,
      confirmationNumber: changed('confirmationNumber')
         ? draft.confirmationNumber
            ? Number(draft.confirmationNumber)
            : undefined
         : undefined,
      location: changed('location') ? draft.location || undefined : undefined,
      tank: changed('tank') ? draft.tank || undefined : undefined,
      operator: changed('operator') ? draft.operator || undefined : undefined,
      leaseId: changed('leaseId') ? (draft.leaseId ? Number(draft.leaseId) : undefined) : undefined,
      countyState: changed('countyState') ? draft.countyState || undefined : undefined,
      purchaser: changed('purchaser') ? draft.purchaser || undefined : undefined,
      transportationCompany: changed('transportationCompany') ? draft.transportationCompany || undefined : undefined,
      pickupDate: changed('pickupDate')
         ? draft.pickupDate
            ? new Date(draft.pickupDate).toISOString()
            : undefined
         : undefined,
      topGauge: changed('topGauge') ? draft.topGauge || undefined : undefined,
      bottomGauge: changed('bottomGauge') ? draft.bottomGauge || undefined : undefined,
      tankBottom: changed('tankBottom') ? (draft.tankBottom ? Number(draft.tankBottom) : undefined) : undefined,
      obsGravity: changed('obsGravity') ? (draft.obsGravity ? Number(draft.obsGravity) : undefined) : undefined,
      correctedGravity: changed('correctedGravity')
         ? draft.correctedGravity
            ? Number(draft.correctedGravity)
            : undefined
         : undefined,
      obsTemperature: changed('obsTemperature')
         ? draft.obsTemperature
            ? Number(draft.obsTemperature)
            : undefined
         : undefined,
      topTemp: changed('topTemp') ? (draft.topTemp ? Number(draft.topTemp) : undefined) : undefined,
      bottomTemp: changed('bottomTemp') ? (draft.bottomTemp ? Number(draft.bottomTemp) : undefined) : undefined,
      bswPct: changed('bswPct') ? bswPct : undefined,
      govBarrels: changed('govBarrels') ? Number(draft.govBarrels || 0) : undefined,
      gsvBarrels: changed('gsvBarrels') ? gsvBarrels : undefined,
      nsvBarrels: nsvBarrelsChanged ? nsvBarrels : undefined,
      sealOff: changed('sealOff') ? draft.sealOff || undefined : undefined,
      sealOffTime: changed('sealOffTime')
         ? draft.sealOffTime
            ? new Date(draft.sealOffTime).toISOString()
            : undefined
         : undefined,
      sealOn: changed('sealOn') ? draft.sealOn || undefined : undefined,
      sealOnTime: changed('sealOnTime')
         ? draft.sealOnTime
            ? new Date(draft.sealOnTime).toISOString()
            : undefined
         : undefined,
      // truck/trailer/sid/odometer/driverName/latitude/longitude RETIRED
      // entirely 2026-08-24 — RunTicketDraft no longer carries them (see
      // types/well.ts), so there's nothing to send here even though the
      // live schema still accepts them; a backend request to actually
      // remove the fields (and stop the eventual AI scan job from
      // populating them) is in
      // docs/run-ticket-transportation-fields-retirement-spec.md.
   };
}

// Every RunTicket field ScanTicketButton's quick-scan flow doesn't have
// real values for yet — AI scanning fills these in once it runs (see
// docs/run-ticket-scan-spec.md); the tech only had to type a ticket
// number and take a photo. NSV/GSV/GOV default to 0 rather than being
// left undefined, matching the same REQUIRED_NUMERIC_FIELD_DEFAULT
// convention dailyReportTypes.ts uses for other NOT NULL-ish fields,
// until the scan overwrites them for real.
//
// Moved here 2026-08-21 (was local to ScanTicketButton.tsx) — that
// component no longer creates the RunTicket itself (see its own doc
// comment on why), so this now lives where its actual callers are:
// DailyReportPage.tsx/DailyReportPanel.tsx build this at Submit Report
// time, using the id ScanTicketButton generated at capture time via its
// onPendingPhotoCaptured callback.
export function blankTicketInput(id: string, wellId: string, ticketNumber: string): RunTicketCreateUpdateInput {
   return {
      id,
      wellId,
      ticketNumber,
      pickupDate: new Date().toISOString(),
      tankBottom: 0,
      obsGravity: 0,
      correctedGravity: 0,
      obsTemperature: 0,
      topTemp: 0,
      bottomTemp: 0,
      bswPct: 0,
      govBarrels: 0,
      gsvBarrels: 0,
      nsvBarrels: 0,
      scanStatus: 'Pending',
   };
}

// ADDED 2026-09-10 per Duane: "When I create a new run ticket offline,
// can it be shown in the list even though not uploaded yet? It doesn't
// now." A ticket created while offline (or online but the request drops
// mid-flight — see useOfflineMutation's own network-error fallback) only
// ever gets queued (state/offlineQueue.ts) — it never touches
// runTicketsAtom, which is owned entirely by useRunTicketReconciliation's
// RunTicket_AllWhere query — so it was invisible on RunTicketsPage until
// the queue actually synced and that query refetched. Same class of
// staleness WellsListPage.tsx already works around for Daily Reports/Mark
// Down (see its own `queue` overlay), but a RunTicket can't be overlaid
// onto an existing row the way those are — a brand-new ticket has no
// existing server row to overlay onto yet. This instead builds a whole
// synthetic RunTicket straight from the queued mutation's own
// RunTicketCreateUpdateInput (RunTicketsPage.handleSaveTicketInner always
// sends every field on create — see toRunTicketInput's own comment on why
// `changed()` is a no-op with no `original` to diff against), so nothing
// here is guessed or defaulted beyond what toRunTicket already does for a
// real row missing an optional field.
//
// wellNameById works the same way toRunTicket's own parameter does —
// callers pass a wellId → wellName lookup (RunTicketsPage already builds
// one off its own `wells`, no separate query needed).
//
// pendingSync: true (see types/well.ts's own doc comment on the field) is
// what lets RunTicketsPage tell this apart from a real row — a real row
// from toRunTicket never sets it, so it's left off entirely here rather
// than explicitly set to undefined.
//
// status defaults to 'Matched', same placeholder toRunTicket uses and for
// the same reason (no Daily Report window to cross-check against here
// either) — moot anyway, since RunTicketsPage always shows a "Not Synced"
// chip instead of this status for a pendingSync row, never t.status
// itself.
//
// photoUrl is deliberately left unset — unlike RunTicketFormPanel (which
// reads state/runTicketPhotoPreview.ts's local object-URL preview to show
// a just-captured photo inside the edit drawer itself), RunTicketsPage's
// list/card rendering never shows a ticket's photo at all, real or
// pending, so there's no reader for this to feed.
export function fromQueuedRunTicketInput(
   input: RunTicketCreateUpdateInput,
   wellNameById: Map<string, string>,
): RunTicket {
   const wellId = input.wellId != null ? String(input.wellId) : '';
   return {
      id: String(input.id),
      wellId,
      wellName: wellNameById.get(wellId) ?? '',
      ticketNumber: input.ticketNumber ?? '',
      confirmationNumber: input.confirmationNumber ?? null,
      location: input.location ?? '',
      tank: input.tank ?? '',
      operator: input.operator ?? '',
      leaseId: input.leaseId ?? null,
      countyState: input.countyState ?? '',
      purchaser: input.purchaser ?? '',
      transportationCompany: input.transportationCompany ?? '',
      pickupDate: input.pickupDate ? String(input.pickupDate) : '',
      topGauge: input.topGauge ?? '',
      bottomGauge: input.bottomGauge ?? '',
      tankBottom: input.tankBottom ?? 0,
      obsGravity: input.obsGravity ?? 0,
      correctedGravity: input.correctedGravity ?? 0,
      obsTemperature: input.obsTemperature ?? 0,
      topTemp: input.topTemp ?? 0,
      bottomTemp: input.bottomTemp ?? 0,
      bswPct: input.bswPct ?? 0,
      govBarrels: input.govBarrels ?? 0,
      gsvBarrels: input.gsvBarrels ?? 0,
      nsvBarrels: input.nsvBarrels ?? 0,
      sealOff: input.sealOff ?? '',
      sealOffTime: input.sealOffTime ? String(input.sealOffTime) : null,
      sealOn: input.sealOn ?? '',
      sealOnTime: input.sealOnTime ? String(input.sealOnTime) : null,
      scanStatus: normalizeScanStatus(input.scanStatus),
      status: 'Matched',
      pendingSync: true,
      // No server-assigned audit data yet — this ticket hasn't synced,
      // so the backend has never actually created a row to stamp
      // createdByUserId/createdDateTime/updatedByUserId/updatedDateTime
      // on. AuditReportPage.tsx's "not tracked" fallback covers this.
      createdByUserId: null,
      createdDateTime: null,
      updatedByUserId: null,
      updatedDateTime: null,
   };
}
